import { useTranslation } from "react-i18next"

import type { FilthSearchActivityState } from "../../utils/filth-search-activity"

import { usePrismSequence } from "@/features/chat/hooks/use-prism-sequence"
import { cn } from "@/lib/utils"

import { PRISM_FLOW_STEP_MS } from "./prism-retrieval-constants"
import { getPrismStepCap } from "./prism-retrieval-utils"
import { PrismStepPills } from "./prism-step-pills"

/** The most space types FILTH reports: company, shared and personal. */
const SPACE_TYPES_MAX = 3

type PrismFlowRowProps = {
  state: FilthSearchActivityState
  isLive: boolean
}

/**
 * Flödesraden: the smallest Prism view, one row of nodes — identity, access,
 * spaces, evidence — so the answer can start right below it. The space and
 * evidence counts appear only once FILTH reports them grounded.
 */
export function PrismFlowRow({ state, isLive }: PrismFlowRowProps) {
  const { t } = useTranslation()
  const total = PRISM_FLOW_STEP_MS.length
  const cap = getPrismStepCap(state, total)
  const sequence = usePrismSequence(isLive, PRISM_FLOW_STEP_MS, { cap, memoryId: "flow" })
  const done = Math.min(sequence.step, cap)
  const active = sequence.running && done < total ? done : -1
  const grounded = state.phase === "grounded"

  const spacesLabel =
    grounded && done > 2 && state.spaceTypes.length > 0
      ? t("activity_messages.filth.spacesCount", {
          count: state.spaceTypes.length,
          defaultValue: "{{count}} spaces",
        })
      : t("activity_messages.filth.steps.spacesShort", { defaultValue: "Spaces" })
  const evidenceLabel =
    grounded && done > 3
      ? t("activity_messages.filth.evidence", {
          count: state.evidenceCount,
          defaultValue: "{{count}} evidence items",
        })
      : t("activity_messages.filth.steps.evidenceShort", { defaultValue: "Evidence" })

  return (
    <div
      className={cn(sequence.motion && "prism-motion")}
      data-prism-animated={sequence.motion}
      data-testid="prism-flow-row"
    >
      <PrismStepPills
        active={active}
        done={done}
        label={t("activity_messages.filth.stepsLabel", { defaultValue: "Prism steps" })}
        steps={[
          {
            key: "identity",
            label: t("activity_messages.filth.steps.identity", { defaultValue: "Identity" }),
          },
          {
            key: "access",
            label: t("activity_messages.filth.steps.access", { defaultValue: "Access" }),
          },
          {
            key: "spaces",
            label: spacesLabel,
            reserve: t("activity_messages.filth.spacesCount", {
              count: SPACE_TYPES_MAX,
              defaultValue: "{{count}} spaces",
            }),
          },
          { key: "evidence", label: evidenceLabel },
        ]}
      />
    </div>
  )
}
